import { useState, useMemo } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { Skeleton } from '@/components/ui/skeleton'
import { Plus, Search, Users } from 'lucide-react'
import { MainLayout } from '@/components/layout'
import { useDocumentTitle } from '@/hooks/use-document-title'
import {
  usePatients,
  useDeletePatient,
  PatientCard,
  PatientRegistrationModal,
  PatientEditModal,
} from '@/features/patients'

export const Route = createFileRoute('/_authenticated/patients/')({
  component: PatientsPage,
})

function PatientsPage() {
  useDocumentTitle('Pacientes')
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [editingPatient, setEditingPatient] = useState<{
    id: string
    full_name: string
    birth_date: string | null
    address: string | null
    emergency_contact: string | null
    emergency_contact_country_code?: string | null
    condition_description: string | null
    medical_history: string | null
    current_medications: string[] | null
    status: 'active' | 'inactive' | 'discharged' | 'deleted'
  } | null>(null)

  const filters = useMemo(
    () => ({
      search: search || undefined,
      status: statusFilter !== 'all' ? (statusFilter as 'active' | 'inactive' | 'discharged') : undefined,
    }),
    [search, statusFilter]
  )

  const { data: patients, isLoading } = usePatients(filters)
  const deletePatient = useDeletePatient()

  const handleDelete = async (id: string) => {
    if (confirm('Tem certeza que deseja excluir este paciente?')) {
      await deletePatient.mutateAsync(id)
    }
  }

  return (
    <MainLayout title="Pacientes" subtitle="Gerencie os pacientes da sua organizacao">
      <div className="space-y-6">
        {/* Header com botao */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-2.5 bg-blue-50 rounded-lg">
              <Users className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">
                {patients?.length ?? 0} pacientes cadastrados
              </p>
            </div>
          </div>
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 shadow-sm"
          >
            <Plus className="mr-2 h-4 w-4" />
            Novo Paciente
          </button>
        </div>

        {/* Filtros */}
        <div className="card p-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="relative flex-1 w-full sm:max-w-sm">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                placeholder="Buscar pacientes..."
                className="w-full pl-10 px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            >
              <option value="all">Todos os status</option>
              <option value="active">Ativos</option>
              <option value="inactive">Inativos</option>
              <option value="discharged">Alta</option>
            </select>
          </div>
        </div>

        {/* Lista de pacientes */}
        {isLoading ? (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="card p-4">
                <div className="flex items-start gap-3">
                  <Skeleton className="h-11 w-11 rounded-full" />
                  <div className="flex-1">
                    <Skeleton className="h-5 w-3/4 mb-2" />
                    <Skeleton className="h-4 w-1/2" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : !patients?.length ? (
          <div className="card p-12 text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-gray-100 rounded-full mb-4">
              <Users className="h-6 w-6 text-gray-400" />
            </div>
            <p className="text-gray-600 font-medium">
              {search || statusFilter !== 'all'
                ? 'Nenhum paciente encontrado com os filtros aplicados.'
                : 'Nenhum paciente cadastrado.'}
            </p>
            <p className="text-sm text-gray-500 mt-1">
              Clique em "Novo Paciente" para adicionar.
            </p>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {patients.map((patient) => (
              <PatientCard
                key={patient.id}
                patient={patient}
                onEdit={() => setEditingPatient(patient)}
                onDelete={() => handleDelete(patient.id)}
              />
            ))}
          </div>
        )}

        <PatientRegistrationModal
          open={isCreateModalOpen}
          onOpenChange={setIsCreateModalOpen}
        />

        <PatientEditModal
          patient={editingPatient}
          open={!!editingPatient}
          onOpenChange={(open) => !open && setEditingPatient(null)}
        />
      </div>
    </MainLayout>
  )
}
